import { useMemo, useRef } from "react";
import Delta from "quill-delta";
import Quill from "@/libraries/quill";
import createDOMPurify from "dompurify";

import { DeltaOperation, DiffPart, TextSidebarDiffProps } from "../../types";

import "../../styles.css";

const valueToDelta = (val: string | Delta): Delta => {
  if (val instanceof Delta) return val;
  const temp = document.createElement("div");
  const DOMPurify = createDOMPurify(window);
  const quill = new Quill(temp, { modules: { toolbar: false } });
  quill.clipboard.dangerouslyPasteHTML(
    DOMPurify.sanitize(val as string, { USE_PROFILES: { html: true } }),
  );
  return quill.getContents();
};

const getDeltaDiffParts = (
  oldValue: string | Delta,
  newValue: string | Delta,
): DiffPart[] => {
  const oldDelta = valueToDelta(oldValue);
  const newDelta = valueToDelta(newValue);
  const diff = oldDelta.diff(newDelta);

  const oldText = oldDelta.ops
    .map((op) => (typeof op.insert === "string" ? op.insert : ""))
    .join("");

  const parts: DiffPart[] = [];
  let oldIndex = 0;

  for (const op of diff.ops) {
    if (op.retain && typeof op.retain === "number") {
      const text = oldText.slice(oldIndex, oldIndex + op.retain);
      parts.push({ op: DeltaOperation.EQUAL, text });
      oldIndex += op.retain;
    } else if (op.delete) {
      const text = oldText.slice(oldIndex, oldIndex + op.delete);
      parts.push({ op: DeltaOperation.DELETE, text });
      oldIndex += op.delete;
    } else if (op.insert && typeof op.insert === "string") {
      parts.push({
        op: DeltaOperation.INSERT,
        text: op.insert,
        attributes: op.attributes,
      });
    }
  }

  return parts;
};

const TextSidebarDiff = ({ oldValue, newValue }: TextSidebarDiffProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const parts = useMemo(
    () => getDeltaDiffParts(oldValue, newValue),
    [oldValue, newValue],
  );

  return (
    <div
      ref={containerRef}
      className="history-rich-text-diff history-inline-plain-diff body-m-regular"
    >
      {parts.map((part, i) => {
        if (part.op === DeltaOperation.DELETE) {
          return (
            <span key={i} className="history-diff-variant--removed">
              {part.text}
            </span>
          );
        }
        if (part.op === DeltaOperation.INSERT) {
          return (
            <span key={i} className="history-diff-variant--added">
              {part.text}
            </span>
          );
        }
        return part.text ? <span key={i}>{part.text}</span> : null;
      })}
    </div>
  );
};

export default TextSidebarDiff;
